/**
 * PlanComplianceModal Component
 * 
 * Modal bloqueante exibido quando usuário está fora de conformidade com o plano atual.
 * Cenário típico: downgrade de Premium → Basic, mas mantém 10 membros (limite Basic = 5).
 * 
 * Comportamento:
 * - Full-screen overlay que impede navegação
 * - Ações disponíveis: Gerenciar Família (excluir membros) ou Assinar plano maior
 * - Não pode ser fechado até que a conformidade seja restaurada
 * 
 * Fase 6 - Bloqueio Pós-Downgrade (zero-Realtime)
 */

import { AlertTriangle, Users, CreditCard } from 'lucide-react';

interface PlanComplianceModalProps {
  currentMembers: number;
  maxMembers: number;
  currentPlan: 'free' | 'basic' | 'premium';
  onManageFamily: () => void;
  onUpgrade: () => void;
}

export function PlanComplianceModal({
  currentMembers,
  maxMembers,
  currentPlan,
  onManageFamily,
  onUpgrade
}: PlanComplianceModalProps) {
  const excess = currentMembers - maxMembers;

  const planNames: Record<string, string> = {
    free: 'Gratuito',
    basic: 'Básico',
    premium: 'Premium'
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl">
        {/* Header */}
        <div className="flex items-start gap-4 mb-6">
          <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-8 h-8 text-orange-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              Limite do Plano Atingido
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Ação necessária para continuar usando o app
            </p>
          </div>
        </div>

        {/* Explicação */}
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-5 mb-6">
          <p className="text-gray-800 mb-3">
            Você está no <strong>Plano {planNames[currentPlan]}</strong> com limite de{' '}
            <strong>{maxMembers} {maxMembers === 1 ? 'membro' : 'membros'}</strong>, mas possui{' '}
            <strong className="text-orange-600">{currentMembers} {currentMembers === 1 ? 'membro' : 'membros'}</strong> cadastrados.
          </p>
          <p className="text-gray-700 text-sm">
            Para continuar usando o Magistral, você precisa:
          </p>
          <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside mt-2 ml-2">
            <li>Remover <strong className="text-orange-600">{excess} {excess === 1 ? 'membro' : 'membros'}</strong> da sua família, ou</li>
            <li>Assinar um plano com mais capacidade</li>
          </ul>
        </div>

        {/* Ações */}
        <div className="space-y-3">
          {/* Opção 1: Gerenciar Família */}
          <button
            onClick={() => {
              onManageFamily();
            }}
            className="w-full flex items-center justify-between p-4 bg-emerald-50 hover:bg-emerald-100 border-2 border-emerald-200 rounded-2xl transition-all group"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-gray-800">Gerenciar Família</p>
                <p className="text-sm text-gray-600">Remover {excess} {excess === 1 ? 'membro' : 'membros'} para adequar ao plano</p>
              </div>
            </div>
            <i className="ri-arrow-right-line text-2xl text-emerald-600"></i>
          </button>

          {/* Opção 2: Assinar Plano */}
          <button
            onClick={() => {
              onUpgrade();
            }}
            className="w-full flex items-center justify-between p-4 bg-purple-50 hover:bg-purple-100 border-2 border-purple-200 rounded-2xl transition-all group"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-gray-800">Assinar Plano Maior</p>
                <p className="text-sm text-gray-600">
                  {currentPlan === 'free' && 'Básico (5 membros) ou Premium (10 membros)'}
                  {currentPlan === 'basic' && 'Premium (10 membros)'}
                </p>
              </div>
            </div>
            <i className="ri-arrow-right-line text-2xl text-purple-600"></i>
          </button>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Este modal não pode ser fechado até que você se adeque ao limite do seu plano.
          </p>
        </div>
      </div>
    </div>
  );
}
